import React, { useState, useMemo } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { CSVLink } from "react-csv";
import { FiShoppingCart, FiCheckCircle, FiClock, FiDollarSign } from "react-icons/fi";

function TotalOrder() {
  // Sample order data
  const [orders] = useState([
    {
      id: 101,
      customerName: "John Doe",
      email: "john@example.com",
      phone: "+91 9876543210",
      totalAmount: 2500,
      items: 5,
      status: "Completed",
      orderDate: "2025-08-15",
      createdAt: "2025-08-10",
    },
    {
      id: 102,
      customerName: "Jane Smith",
      email: "jane@example.com",
      phone: "+91 9123456780",
      totalAmount: 1800,
      items: 3,
      status: "Pending",
      orderDate: "2025-08-20",
      createdAt: "2025-08-18",
    },
    {
      id: 103,
      customerName: "Mike Johnson",
      email: "mike@example.com",
      phone: "+91 9988776655",
      totalAmount: 4200,
      items: 7,
      status: "Completed",
      orderDate: "2025-08-22",
      createdAt: "2025-08-20",
    },
  ]);

  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    dateFrom: null,
    dateTo: null,
  });

  const [showFilter, setShowFilter] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setFilters({ ...filters, [name]: date[0] });
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch =
        o.customerName.toLowerCase().includes(filters.search.toLowerCase()) ||
        o.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        o.phone.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === "All" || o.status === filters.status;

      const matchesDate =
        (!filters.dateFrom || new Date(o.orderDate) >= filters.dateFrom) &&
        (!filters.dateTo || new Date(o.orderDate) <= filters.dateTo);

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [orders, filters]);

  const statuses = ["All", ...new Set(orders.map((o) => o.status))];

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  // Report Summary
  const reportSummary = {
    totalOrders: filteredOrders.length,
    completed: filteredOrders.filter((o) => o.status === "Completed").length,
    pending: filteredOrders.filter((o) => o.status === "Pending").length,
    totalAmount: filteredOrders.reduce((sum, o) => sum + o.totalAmount, 0),
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title">Total Orders</h4>
            <div>
              <button
                className="btn btn-primary me-2"
                onClick={() => setShowFilter(true)}
              >
                Filters
              </button>
              <CSVLink
                data={filteredOrders}
                filename={"total-orders.csv"}
                className="btn btn-primary"
              >
                Export CSV
              </CSVLink>
            </div>
          </div>

          {/* Report Summary */}
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="kpi-card bg-soft-primary d-flex align-items-center p-3">
                <FiShoppingCart size={30} className="text-primary" />
                <div className="kpi-content ms-3">
                  <h6>Total Orders</h6>
                  <h2>{reportSummary.totalOrders}</h2>
                  <p className="text-success">↑ 10% from last month</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="kpi-card bg-soft-primary d-flex align-items-center p-3">
                <FiCheckCircle size={30} className="text-success" />
                <div className="kpi-content ms-3">
                  <h6>Completed</h6>
                  <h2>{reportSummary.completed}</h2>
                  <p className="text-success">↑ 5% from last month</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="kpi-card bg-soft-primary d-flex align-items-center p-3">
                <FiClock size={30} className="text-warning" />
                <div className="kpi-content ms-3">
                  <h6>Pending</h6>
                  <h2>{reportSummary.pending}</h2>
                  <p className="text-danger">↓ 2% from last month</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="kpi-card bg-soft-primary d-flex align-items-center p-3">
                <FiDollarSign size={30} className="text-info" />
                <div className="kpi-content ms-3">
                  <h6>Total Amount</h6>
                  <h2>₹{reportSummary.totalAmount}</h2>
                  <p className="text-success">↑ 8% from last month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body table-responsive">
                <table className="table table-hover table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Items</th>
                      <th>Total Amount</th>
                      <th>Status</th>
                      <th>Order Date</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((o) => (
                      <tr key={o.id}>
                        <td>{o.id}</td>
                        <td>{o.customerName}</td>
                        <td>{o.email}</td>
                        <td>{o.phone}</td>
                        <td>{o.items}</td>
                        <td>₹{o.totalAmount}</td>
                        <td>
                          <span
                            className={`badge ${
                              o.status === "Completed"
                                ? "bg-success"
                                : o.status === "Pending"
                                ? "bg-warning"
                                : "bg-secondary"
                            }`}
                          >
                            {o.status}
                          </span>
                        </td>
                        <td>{o.orderDate}</td>
                        <td>{o.createdAt}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button className="btn btn-sm btn-primary">View</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Rows per page:{" "}
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[5, 10, 20, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          className={`btn btn-sm ${
                            currentPage === page
                              ? "btn-primary"
                              : "btn-outline-primary"
                          } mx-1`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          <div className={`filter-panel ${showFilter ? "open" : ""}`}>
            <div className="filter-header">
              <h5>Filters</h5>
              <button
                className="btn-close"
                onClick={() => setShowFilter(false)}
              ></button>
            </div>
            <div className="filter-body">
              <div className="mb-3">
                <label>Search</label>
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Customer name, email or phone"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="mb-3">
                <label>Status</label>
                <select
                  className="form-control"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label>Order From</label>
                <Flatpickr
                  value={filters.dateFrom}
                  onChange={(date) => handleDateChange("dateFrom", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                />
              </div>
              <div className="mb-3">
                <label>Order To</label>
                <Flatpickr
                  value={filters.dateTo}
                  onChange={(date) => handleDateChange("dateTo", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                />
              </div>
              <button
                className="btn btn-primary w-100"
                onClick={() => setShowFilter(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default TotalOrder;
